import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery, useTheme } from '@mui/material';

import { BenefitDto } from 'theSource/queries';
import { Beneficiaries } from 'enrollment/features/AddYourBeneficiaries/components/Beneficiaries';
import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';
import { useEnrollmentBeneficiaryQuery } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery';
import { filteredBeneficiaries } from '../utils';
import { filterBeneficiaries } from 'enrollment/utils/common/Utils';
import { setBeneficiarySelections } from 'enrollment/features/AddYourBeneficiaries/slice';
import {
  setAllBeneficiaryData,
  selectNewBeneficiary,
} from 'enrollment/features/AddYourBeneficiaries/services';

interface BeneficiariesSectionProps {
  benefits: BenefitDto[];
  setDisabled: any;
}

export const BeneficiariesSection = ({
  benefits,
  setDisabled,
}: BeneficiariesSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');
  const desktopViewConditions = useMediaQuery(theme.breakpoints.up('sm'));
  const dispatch = useDispatch();

  const updateBeneficiaries = (data: BenefitDto[] | undefined) => {
    const initialPlansState = filteredBeneficiaries(data)?.filter(
      (BeneficiaryData) =>
        BeneficiaryData !== null && BeneficiaryData !== undefined
    );
    initialPlansState?.length &&
      dispatch(setAllBeneficiaryData(initialPlansState));
  };

  useEffect(() => {
    if (benefits?.length) {
      updateBeneficiaries(benefits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benefits]);

  const { data: beneficiaryDetails, isLoading: isBeneficiaryLoading } =
    useEnrollmentBeneficiaryQuery({
      selectionOptions: true,
    });

  useEffect(() => {
    if (beneficiaryDetails) {
      dispatch(
        setBeneficiarySelections(
          beneficiaryDetails?.data?.beneficiary_selection_options
        )
      );
    }
  }, [beneficiaryDetails, dispatch]);

  const metadata = beneficiaryDetails?.data?.metadata;
  const enrollmentState = useSelector((state: any) => state?.enrollment);
  const allBeneficiaries =
    enrollmentState?.storeBeneficiarySelection?.beneficiarySelectionOptions ||
    [];

  const benefitPlansState = useSelector(selectNewBeneficiary).beneficiaryData;
  const emptyPrimaryBeneficiaries = benefitPlansState
    ?.map((benefitPlan: any) => {
      return (
        filterBeneficiaries(benefitPlan?.beneficiaries, true)?.length === 0
      );
    })
    ?.includes(true);

  return (
    <div data-testid="beneficiaries-section">
      {benefits?.map((benefit: BenefitDto) => {
        return (
          <div
            className="card px-4 py-4"
            key={benefit?.org_benefit_id}
            id={`coverage-plan-${benefit?.org_benefit_id}`}
          >
            <div className="d-flex flex-column-reverse flex-md-row align-items-center mb-3_5">
              <div className="flex-fill">
                <h2 className="headline-3">{benefit?.org_benefit_name}</h2>
                {!benefit.hide_volume_on_confirm && (
                  <p className="body2">
                    {t('planCoverageAmount')}:{' '}
                    <span className="semiBold">
                      {getPremiumStyled({
                        premium: benefit.coverage.req_volume,
                        valueOnly: RETURN_VALUE_ONLY,
                      })}
                    </span>
                  </p>
                )}
              </div>
              <div className="text-center text-md-right">
                {benefit.coverage?.carrier_logo_url ? (
                  <img
                    src={benefit.coverage?.carrier_logo_url}
                    alt="Carrier Logo"
                    data-testid="logo"
                  />
                ) : null}

                <Typography variant="body2" sx={{ width: '20rem' }}>
                  <span className="semiBold">
                    {benefit?.coverage?.carrier_name}
                  </span>
                </Typography>
              </div>
            </div>

            <>
              {/* {desktopViewConditions && <Divider orientation="horizontal" />} */}
              <Beneficiaries
                key={`primary-beneficiary-${benefit?.org_benefit_id}`}
                orgBenefitId={benefit?.org_benefit_id}
                isPrimary={true}
                setDisabled={setDisabled}
                emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
                coverageFields={benefit?.coverage}
                allBeneficiaries={allBeneficiaries}
                metadata={metadata}
                isBeneficiaryLoading={isBeneficiaryLoading}
                autoSave={false}
              />
              {desktopViewConditions && <hr className="mx-0 mb-4_5" />}
              <Beneficiaries
                key={`secondary-beneficiary-${benefit?.org_benefit_id}`}
                orgBenefitId={benefit?.org_benefit_id}
                isPrimary={false}
                setDisabled={setDisabled}
                emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
                coverageFields={benefit.coverage}
                allBeneficiaries={allBeneficiaries}
                metadata={metadata}
                isBeneficiaryLoading={isBeneficiaryLoading}
                autoSave={false}
              />
            </>
          </div>
        );
      })}
    </div>
  );
};
